import { useEffect, useState, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, ArrowRightLeft } from "lucide-react";
import ProductCard from "./ProductCard";
import * as productsApi from "../api/productsApi";
import { useLanguage } from "../context/LanguageContext";
import "./ProductRecommendations.css";

function normalizeProduct(p, badge) {
  const sortedImages = (p.productImages || [])
    .slice()
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  return {
    id: p.id,
    name: p.name,
    arabicName: p.arabicName,
    arabicDescription: p.arabicDescription,
    description: p.description,
    price: Number(p.basePrice || p.price || 0),
    imageUrl: p.thumbnailImage || sortedImages[0]?.url || p.imageUrl,
    color: p.colorVariants?.[0]?.colorHex || p.color || "#d8cfc0",
    borderColor: "#c9a96e",
    badge: badge || null,
    unitsSold: p.unitsSold || 0,
    source: "api",
  };
}

export default function ProductRecommendations({ currentProduct, onAddToCart }) {
  const { t, isRTL, language } = useLanguage();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scroll state & gestures
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Mouse Drag Tracking
  const isMouseDownRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const hasDraggedRef = useRef(false);

  // ── Fetch & Compute Recommendations ──────────────────────────────
  useEffect(() => {
    if (!currentProduct?.id) return;
    let cancelled = false;

    async function loadRecommendations() {
      setLoading(true);
      try {
        const [collections, allProducts] = await Promise.all([
          productsApi.fetchCollections().catch(() => []),
          productsApi.fetchProducts().catch(() => []),
        ]);

        if (cancelled) return;

        const safeCollections = Array.isArray(collections) ? collections : [];
        const safeAllProducts = Array.isArray(allProducts) ? allProducts : [];
        const currentId = String(currentProduct.id).toLowerCase();

        // Fetch products for each collection to map relations
        const collectionProductMap = await Promise.all(
          safeCollections.map(async (col) => {
            try {
              const prods = await productsApi.fetchProducts(col.name);
              return {
                collection: col,
                products: Array.isArray(prods) ? prods : [],
              };
            } catch {
              return { collection: col, products: [] };
            }
          })
        );

        if (cancelled) return;

        // Find collections that contain this current product
        const containing = collectionProductMap.filter((cp) =>
          cp.products.some((p) => String(p.id).toLowerCase() === currentId)
        );

        let result = [];

        if (containing.length > 0) {
          // Product is in one or more collections
          const primary = containing[0];
          const sameCollectionOthers = primary.products.filter(
            (p) => String(p.id).toLowerCase() !== currentId
          );

          if (sameCollectionOthers.length > 0) {
            // Case A: Show other products in the same collection + 1 from each other collection
            const sameColItems = sameCollectionOthers.map((p) =>
              normalizeProduct(p, t("product.sameCollectionBadge"))
            );

            const otherCols = collectionProductMap.filter(
              (cp) => cp.collection.id !== primary.collection.id
            );

            const otherColPicks = [];
            for (const otherCp of otherCols) {
              const pick = otherCp.products.find(
                (p) =>
                  String(p.id).toLowerCase() !== currentId &&
                  !sameCollectionOthers.some((s) => String(s.id).toLowerCase() === String(p.id).toLowerCase()) &&
                  !otherColPicks.some((o) => String(o.id).toLowerCase() === String(p.id).toLowerCase())
              );

              if (pick) {
                const colTitle =
                  (isRTL || language === "ar") && otherCp.collection.arabicName?.trim()
                    ? otherCp.collection.arabicName.trim()
                    : otherCp.collection.name;

                otherColPicks.push(
                  normalizeProduct(pick, colTitle)
                );
              }
            }

            result = [...sameColItems, ...otherColPicks];
          } else {
            // Case B1: Product is in a collection, but it's the only product in that collection (single collection)
            // Show all products from all collections
            result = safeAllProducts
              .filter((p) => String(p.id).toLowerCase() !== currentId)
              .map((p) => normalizeProduct(p));
          }
        } else {
          // Case B2: Product is in no collection
          // Show all products from all collections
          result = safeAllProducts
            .filter((p) => String(p.id).toLowerCase() !== currentId)
            .map((p) => normalizeProduct(p));
        }

        // Deduplicate items by ID
        const uniqueMap = new Map();
        result.forEach((p) => {
          if (!uniqueMap.has(p.id)) {
            uniqueMap.set(p.id, p);
          }
        });

        if (!cancelled) {
          setRecommendations(Array.from(uniqueMap.values()));
        }
      } catch (err) {
        console.error("Failed to load recommendations:", err);
        if (!cancelled) setRecommendations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadRecommendations();

    return () => {
      cancelled = true;
    };
  }, [currentProduct?.id, isRTL, language, t]);

  // ── Scroll Progress & Boundary Calculation ─────────────────────────
  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      setScrollProgress(100);
      return;
    }

    const currentScrollAbs = Math.abs(el.scrollLeft);
    const progress = Math.min(100, Math.max(0, (currentScrollAbs / maxScroll) * 100));
    setScrollProgress(progress);

    if (isRTL) {
      // In RTL browsers:
      // The track starts at the right edge.
      // Scrolling towards the left makes el.scrollLeft negative (or decreases from maxScroll).
      // Physically, the Left button moves content to reveal what's to the left:
      // it can only scroll left if we haven't reached the left-most edge (currentScrollAbs < maxScroll - 8).
      // Physically, the Right button moves content back towards the right (starting position):
      // it can only scroll right if we have scrolled left (currentScrollAbs > 8).
      setCanScrollLeft(currentScrollAbs < maxScroll - 8);
      setCanScrollRight(currentScrollAbs > 8);
    } else {
      // In LTR:
      // The track starts at the left edge.
      // Left button moves content back towards the left: enabled if el.scrollLeft > 8.
      // Right button moves content forward to the right: enabled if el.scrollLeft < maxScroll - 8.
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft < maxScroll - 8);
    }
  }, [isRTL]);

  useEffect(() => {
    updateScrollState();
    const el = trackRef.current;
    if (!el) return;
    const handleScroll = () => updateScrollState();
    el.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [recommendations, updateScrollState]);

  // ── Physical Left / Right Scroll Handlers ─────────────────────────
  // Clicking the physical Left button always moves the view towards the left.
  const handleScrollLeft = () => {
    const el = trackRef.current;
    if (!el) return;
    const slide = el.querySelector(".pd-carousel__slide");
    const step = slide ? slide.offsetWidth + 14 : 200;
    el.scrollBy({ left: -step, behavior: "smooth" });
  };

  // Clicking the physical Right button always moves the view towards the right.
  const handleScrollRight = () => {
    const el = trackRef.current;
    if (!el) return;
    const slide = el.querySelector(".pd-carousel__slide");
    const step = slide ? slide.offsetWidth + 14 : 200;
    el.scrollBy({ left: step, behavior: "smooth" });
  };

  // ── Mouse Drag Mechanics ───────────────────────────────────────────
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    const el = trackRef.current;
    if (!el) return;
    isMouseDownRef.current = true;
    hasDraggedRef.current = false;
    startXRef.current = e.pageX - el.offsetLeft;
    scrollLeftRef.current = el.scrollLeft;
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isMouseDownRef.current) return;
    const el = trackRef.current;
    if (!el) return;
    const x = e.pageX - el.offsetLeft;
    const walk = (x - startXRef.current) * 1.35;
    if (Math.abs(walk) > 6) {
      hasDraggedRef.current = true;
    }
    el.scrollLeft = scrollLeftRef.current - walk;
  };

  const handleMouseUpOrLeave = () => {
    if (isMouseDownRef.current) {
      isMouseDownRef.current = false;
      setIsDragging(false);
      setTimeout(() => {
        hasDraggedRef.current = false;
      }, 60);
    }
  };

  const handleClickCapture = (e) => {
    if (hasDraggedRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // If no recommendations and not loading, don't render empty section
  if (!loading && recommendations.length === 0) {
    return null;
  }

  return (
    <section
      className="pd-recommendations"
      aria-label={t("product.recommendedTitle")}
      role="region"
    >
      <div className="container pd-recommendations__inner">
        {/* Header (Fully Centered) */}
        <div className="pd-recommendations__header">
          <div className="pd-recommendations__titles">
            <h2 className="pd-recommendations__title">{t("product.recommendedTitle")}</h2>
          </div>
        </div>

        {/* Carousel Viewport with Flanking Left & Right Buttons */}
        <div className="pd-carousel">
          {recommendations.length > 1 && (
            <button
              type="button"
              className="pd-carousel__arrow pd-carousel__arrow--left"
              onClick={handleScrollLeft}
              disabled={!canScrollLeft}
              aria-label={isRTL ? "التمرير لليسار" : "Scroll left"}
              title={isRTL ? "التمرير لليسار" : "Scroll left"}
            >
              <ChevronLeft size={18} />
            </button>
          )}

          <div
            ref={trackRef}
            className={`pd-carousel__track ${isDragging ? "is-dragging" : ""}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUpOrLeave}
            onMouseLeave={handleMouseUpOrLeave}
            onClickCapture={handleClickCapture}
            tabIndex={0}
            role="list"
            aria-label={t("product.recommendedTitle")}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") handleScrollLeft();
              if (e.key === "ArrowRight") handleScrollRight();
            }}
          >
            {recommendations.map((item) => (
              <div
                key={item.id}
                className="pd-carousel__slide"
                role="listitem"
              >
                <ProductCard product={item} onAddToCart={onAddToCart} />
              </div>
            ))}
          </div>

          {recommendations.length > 1 && (
            <button
              type="button"
              className="pd-carousel__arrow pd-carousel__arrow--right"
              onClick={handleScrollRight}
              disabled={!canScrollRight}
              aria-label={isRTL ? "التمرير لليمين" : "Scroll right"}
              title={isRTL ? "التمرير لليمين" : "Scroll right"}
            >
              <ChevronRight size={18} />
            </button>
          )}
        </div>

        {/* Footer with Dynamic Progress Indicator & Mobile Hint */}
        {recommendations.length > 1 && (
          <div className="pd-carousel__footer">
            <span className="pd-recommendations__counter">
              {recommendations.length}{" "}
              {isRTL ? "قطع مقترحة" : "curated pieces"}
            </span>

            <div className="pd-carousel__progress-bar" aria-hidden="true">
              <div
                className="pd-carousel__progress-fill"
                style={{
                  width: `${Math.max(15, scrollProgress)}%`,
                  transformOrigin: isRTL ? "right" : "left",
                }}
              />
            </div>

            <div className="pd-carousel__swipe-hint">
              <ArrowRightLeft size={13} />
              <span>{t("product.swipeHint")}</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
